// src/features/workflows/NotificationsBell.jsx
//
// WORKFLOWS-SLICE-3: in-app notifications bell for workflow assignment
// events (phase_assigned, substep_assigned, substep_completed,
// phase_completed, run_completed). Polls on mount + when opened.

import React, { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { T } from "../../app/tokens.js";
import {
  getWorkflowNotifications,
  markWorkflowNotificationRead,
  markAllWorkflowNotificationsRead,
} from "../../data/workflowsClient.js";
import { log } from "../../utils/logger.js";

function fmtWhen(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function NotificationsBell({ onOpenRun }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const list = await getWorkflowNotifications(false);
      setNotifications(list || []);
      setUnreadCount((list || []).filter((n) => !n.readAt).length);
    } catch (err) {
      log.error("NotificationsBell: failed to load notifications", err);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  async function handleOpenNotification(n) {
    if (!n.readAt) {
      try {
        await markWorkflowNotificationRead(n.id);
      } catch (err) {
        log.error("NotificationsBell: failed to mark read", err);
      }
    }
    setOpen(false);
    load();
    if (n.runId && onOpenRun) onOpenRun(n.runId);
  }

  async function handleMarkAllRead() {
    try {
      await markAllWorkflowNotificationsRead();
      load();
    } catch (err) {
      log.error("NotificationsBell: failed to mark all read", err);
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        data-testid="workflows-notifications-bell"
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "relative", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
          width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
        }}
      >
        <Bell size={16} color={T.dark} />
        {unreadCount > 0 && (
          <span
            data-testid="workflows-notifications-unread-count"
            style={{
              position: "absolute", top: -4, right: -4, background: T.red, color: T.white,
              fontSize: T.fsXxs, fontWeight: T.fwBold, borderRadius: 999, minWidth: 16, height: 16,
              display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="workflows-notifications-panel"
          style={{
            position: "absolute", right: 0, top: 42, width: 340, maxHeight: 400, overflowY: "auto",
            background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(15,23,42,0.14)",
            zIndex: 30,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="workflows-notifications-mark-all-read"
                onClick={handleMarkAllRead}
                style={{ background: "none", border: "none", color: T.blue, fontSize: T.fsXs, cursor: "pointer" }}
              >
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: 16, fontSize: T.fsXs, color: T.mid, textAlign: "center" }}>No notifications yet.</div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`workflows-notification-${n.id}`}
                onClick={() => handleOpenNotification(n)}
                style={{
                  display: "block", width: "100%", textAlign: "left", background: n.readAt ? "transparent" : T.blueSoft,
                  border: "none", borderBottom: `1px solid ${T.border}`, padding: "10px 12px", cursor: "pointer",
                }}
              >
                <div style={{ fontSize: T.fsXs, color: T.dark, fontWeight: n.readAt ? T.fwRegular : T.fwSemibold }}>{n.title}</div>
                {n.body && <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>{n.body}</div>}
                <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>{fmtWhen(n.createdAt)}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
